import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { State } from "../../models/state";
import { RoutePaths } from "../../routes";
import { StateService } from "../../services/state-services";

interface AdminEditClerkFormProps {
	onSubmit: (values: any) => void;
	mode: "edit" | "create";
	form: any;
}

const AdminEditClerkForm: React.FC<AdminEditClerkFormProps> = ({ onSubmit, mode, form }) => {
	const { register, handleSubmit, formState, getValues } = form;
	const [states, setStates] = useState<State[]>([]);

	useEffect(() => {
		(async () => setStates((await StateService.getActiveStates()) || []))();
	}, []);

	return (
		<form
			className="container mx-auto max-w-[600px] mt-[60px] mb-[100px] flex flex-col gap-[30px]"
			onSubmit={handleSubmit(onSubmit)}
		>
			<div className="flex gap-5">
				<div className="flex flex-col w-1/2">
					<label
						htmlFor="firstName"
						className="uppercase font-medium tracking-widest mb-[10px]"
					>
						First Name
					</label>
					<input
						{...register("firstName")}
						type="text"
						name="firstName"
						placeholder="First Name"
						className={classNames(
							"px-[15px] py-3 border rounded-md border-primary-blue",
							{
								"border-red-500 focus:outline-red-500":
									formState?.errors?.firstName,
							}
						)}
					/>
					<div className="mt-1 text-sm text-red-500">
						{formState?.errors?.firstName?.message}
					</div>
				</div>
				<div className="flex flex-col w-1/2">
					<label
						htmlFor="lastName"
						className="uppercase font-medium tracking-widest mb-[10px]"
					>
						Last Name
					</label>
					<input
						{...register("lastName")}
						type="text"
						name="lastName"
						placeholder="Last Name"
						className={classNames(
							"px-[15px] py-3 border rounded-md border-primary-blue",
							{
								"border-red-500 focus:outline-red-500":
									formState?.errors?.lastName,
							}
						)}
					/>
					<div className="mt-1 text-sm text-red-500">
						{formState?.errors?.lastName?.message}
					</div>
				</div>
			</div>
			<div className="w-full flex flex-col gap-[10px]">
				<label
					htmlFor="email"
					className="font-medium tracking-widest uppercase"
				>
					Email
				</label>
				<input
					{...register("email")}
					name="email"
					type="text"
					autoComplete="off"
					placeholder="Email"
					className={classNames(
						"px-[15px] py-3 w-full border rounded-md border-primary-blue",
						{
							"border-red-500 focus:outline-red-500": formState?.errors?.email,
						}
					)}
				/>
				<div className="-mt-1 text-sm text-red-500">
					{formState?.errors?.email?.message}
				</div>
			</div>
			<div className="w-full flex flex-col gap-[10px]">
				<label
					htmlFor="phone"
					className="font-medium tracking-widest uppercase"
				>
					Phone
				</label>
				<input
					{...register("phone")}
					name="phone"
					type="tel"
					defaultValue="+1"
					placeholder="Phone"
					className={classNames(
						"px-[15px] py-3 w-full border rounded-md border-primary-blue",
						{
							"border-red-500 focus:outline-red-500": formState?.errors?.phone,
						}
					)}
				/>
				<div className="-mt-1 text-sm text-red-500">
					{formState?.errors?.phone?.message}
				</div>
			</div>
			<div className="w-full flex flex-col gap-[10px]">
				<label
					htmlFor="county"
					className="font-medium tracking-widest uppercase"
				>
					County
				</label>
				<input
					{...register("county")}
					name="county"
					type="text"
					placeholder="County"
					className={classNames(
						"px-[15px] py-3 w-full border rounded-md border-primary-blue",
						{
							"border-red-500 focus:outline-red-500": formState?.errors?.county,
						}
					)}
				/>
				<div className="-mt-1 text-sm text-red-500">
					{formState?.errors?.county?.message}
				</div>
			</div>
			<div className="w-full flex flex-col gap-[10px]">
				<label
					htmlFor="municipality"
					className="font-medium tracking-widest uppercase"
				>
					Municipality
				</label>
				<input
					{...register("municipality")}
					name="municipality"
					type="text"
					placeholder="Municipality"
					className={classNames(
						"px-[15px] py-3 w-full border rounded-md border-primary-blue",
						{
							"border-red-500 focus:outline-red-500":
								formState?.errors?.municipality,
						}
					)}
				/>
				<div className="-mt-1 text-sm text-red-500">
					{formState?.errors?.municipality?.message}
				</div>
			</div>
			<div className="w-full flex flex-col gap-[10px]">
				<label
					htmlFor="streetAddress"
					className="font-medium tracking-widest uppercase"
				>
					Business Address
				</label>
				<input
					{...register("streetAddress")}
					name="streetAddress"
					type="text"
					placeholder="Street Address"
					className={classNames(
						"px-[15px] py-3 w-full border rounded-md border-primary-blue",
						{
							"border-red-500 focus:outline-red-500":
								formState?.errors?.streetAddress,
						}
					)}
				/>
				<div className="-mt-1 text-sm text-red-500">
					{formState?.errors?.streetAddress?.message}
				</div>
				<input
					{...register("aptSuiteBuilding")}
					type="text"
					placeholder="Apt, Suite, Building"
					className="px-[15px] py-3 w-full border rounded-md border-primary-blue mt-[5px]"
				/>
				<input
					{...register("city")}
					type="text"
					placeholder="City"
					className={classNames(
						"px-[15px] py-3 w-full border rounded-md border-primary-blue mt-[5px]",
						{
							"border-red-500 focus:outline-red-500": formState?.errors?.city,
						}
					)}
				/>
				<div className="-mt-1 text-sm text-red-500">
					{formState?.errors?.city?.message}
				</div>
				<div className="flex gap-5 mt-[5px]">
					<div className="w-1/2">
						<select
							{...register("state")}
							disabled={states.length === 0}
							className={classNames(
								"px-[15px] py-3 w-full border rounded-md border-primary-blue hover:cursor-pointer",
								{
									"border-red-500 focus:outline-red-500":
										formState?.errors?.state,
								}
							)}
						>
							<option value="" disabled selected>
								State
							</option>
							{states.map((state) => (
								<option key={`state-${state.id}`} value={state.id}>
									{state.name}
								</option>
							))}
						</select>
						<div className="mt-1 text-sm text-red-500">
							{formState?.errors?.state?.message}
						</div>
					</div>
					<div className="w-1/2">
						<input
							{...register("zipCode")}
							type="text"
							placeholder="Zip Code"
							className={classNames(
								"px-[15px] py-3 w-full border rounded-md border-primary-blue",
								{
									"border-red-500 focus:outline-red-500":
										formState?.errors?.zipCode,
								}
							)}
						/>
						<div className="mt-1 text-sm text-red-500">
							{formState?.errors?.zipCode?.message}
						</div>
					</div>
				</div>
			</div>
			<div className="w-full flex justify-end gap-[15px]">
				<a
					className="uppercase tracking-[0.07rem] px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-transparent hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
					href={'/clerks'}
				>
					Cancel
				</a>
				<button
					type="submit"
					onClick={() => console.log(formState)}
					disabled={
						(formState.isSubmitting && !formState.errors) &&
						formState.isSubmitted
					}
					className={classNames(
						"uppercase inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 text-white border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500",
						{
							"opacity-40 cursor-not-allowed":
								(formState.isSubmitting && !formState.errors) &&
								formState.isSubmitted,
						}
					)}
				>
					Save
				</button>
			</div>
		</form>
	);
};

export default AdminEditClerkForm;
